import { clearTokens, getAccessToken, getRefreshToken, setTokens, type Tokens } from './session'

const ROOT_URL = import.meta.env.VITE_API_URL ?? ''
const BASE_URL = `${ROOT_URL}/api/v1`

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /** The server's `error.details`, when it sent any (e.g. why a Telegram channel is unreachable). */
  readonly details: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

export class NetworkError extends Error {
  constructor() {
    super('network_unavailable')
    this.name = 'NetworkError'
  }
}

let refreshInFlight: Promise<boolean> | null = null

/**
 * A stable id for this browser, sent with the refresh. The API answers a replay
 * of a just-rotated refresh token (a second panel tab presenting the token the
 * first one already used) with the same successor only when it comes from the
 * installation that rotated it; without the id the replay is treated as theft
 * and every session of the operator ends.
 */
const DEVICE_KEY = 'admin_device_id'
function deviceId(): string | null {
  try {
    let id = localStorage.getItem(DEVICE_KEY)
    if (id === null || !/^[A-Za-z0-9_-]{8,64}$/.test(id)) {
      const random =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`
      id = random.replace(/-/g, '')
      localStorage.setItem(DEVICE_KEY, id)
    }
    return id
  } catch {
    return null
  }
}

async function performRefresh(): Promise<boolean> {
  const token = getRefreshToken()
  if (token === null) return false
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  const device = deviceId()
  if (device !== null) headers['x-device-id'] = device
  try {
    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refreshToken: token }),
    })
    if (!response.ok) {
      clearTokens()
      return false
    }
    // The API wraps the pair: `{ tokens: { accessToken, refreshToken } }`.
    const body = (await response.json()) as { tokens?: Tokens } | Tokens
    const tokens = 'tokens' in body && body.tokens !== undefined ? body.tokens : (body as Tokens)
    if (typeof tokens.accessToken !== 'string' || typeof tokens.refreshToken !== 'string') {
      clearTokens()
      return false
    }
    setTokens(tokens)
    return true
  } catch {
    // A network blip must not sign an operator out mid-approval.
    return false
  }
}

/** One rotation at a time; a replayed refresh token revokes the whole family. */
function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  anonymous?: boolean
  /** Sent as Idempotency-Key so a retried approval cannot pay twice. */
  idempotencyKey?: string
  /** The one-time code for decisions that require the second factor. */
  totpCode?: string
  /** The path lives at the server root (health, metrics), not under /api/v1. */
  root?: boolean
}

function buildUrl(path: string, options: RequestOptions): string {
  const base = options.root ? ROOT_URL : BASE_URL
  if (!options.query) return base + path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(options.query)) {
    if (value !== undefined) params.set(key, String(value))
  }
  const qs = params.toString()
  return qs === '' ? base + path : `${base}${path}?${qs}`
}

async function send(path: string, options: RequestOptions, retrying: boolean): Promise<Response> {
  const headers: Record<string, string> = {}
  if (options.body !== undefined) headers['content-type'] = 'application/json'
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey
  if (options.totpCode) headers['x-totp-code'] = options.totpCode

  const token = getAccessToken()
  if (!options.anonymous && token !== null) headers.authorization = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(buildUrl(path, options), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    })
  } catch {
    throw new NetworkError()
  }

  if (response.status === 401 && !options.anonymous && !retrying) {
    if (await refreshOnce()) return send(path, options, true)
  }
  return response
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options, false)
  if (response.status === 204) return undefined as T

  const text = await response.text()
  const parsed: unknown = text === '' ? null : JSON.parse(text)

  if (!response.ok) {
    const body = parsed as { error?: { code?: string; message?: string; details?: unknown } } | null
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? response.statusText,
      body?.error?.details,
    )
  }
  return parsed as T
}

/** An authenticated file, for scans that must never sit behind a public URL. */
export async function download(path: string): Promise<Blob> {
  const response = await send(path, {}, false)
  if (!response.ok) throw new ApiError(response.status, 'DOWNLOAD_FAILED', response.statusText)
  return response.blob()
}

/** The raw authenticated response, for files whose headers matter (CSV exports). */
async function raw(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<Response> {
  const response = await send(path, { ...options, method: 'GET' }, false)
  if (!response.ok) {
    const text = await response.text()
    let code = 'DOWNLOAD_FAILED'
    let message = response.statusText
    try {
      const body = JSON.parse(text) as { error?: { code?: string; message?: string } }
      code = body.error?.code ?? code
      message = body.error?.message ?? message
    } catch {
      // Not JSON; the status line is the message.
    }
    throw new ApiError(response.status, code, message)
  }
  return response
}

export const api = {
  raw,
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
